import { ReactNode } from 'react'
import { NotificationInstance } from 'antd/es/notification/interface'
import {
  createEffect,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { fx, path } from '../../libs'
import { Counters, Error } from './types'

export type NotificationItem = {
  key: string
  count: number
  color: string
  path: string
  filter?: string
}

export type NotificationPayload = {
  type: 'success' | 'info' | 'warning' | 'error'
  description: ReactNode
  message?: string
  placement?: 'topRight' | 'topLeft' | 'bottomRight' | 'bottomLeft'
  duration?: number
}

const mapCounterObject = {
  PendingIn: {
    key: 'deposit-pending-manager',
    color: '#F9B115',
    path: '/deposit',
    filter: '?status=TRANSACTION_REQUEST_STATUS_PENDING_MANAGER',
  },
  PendingOut: {
    key: 'withdrawal-pending-manager',
    color: '#F9B115',
    path: '/withdrawal',
    filter: '?status=TRANSACTION_REQUEST_STATUS_PENDING_MANAGER',
  },
  VendorErrOut: {
    key: 'withdrawal-vendor-error',
    color: '#E55353',
    path: '/withdrawal',
    filter: '?status=TRANSACTION_REQUEST_STATUS_VENDOR_ERROR',
  },
  VerificationReqCnt: {
    key: 'verification-new',
    color: '#F9B115',
    path: path.verificationsV2.entry(),
    filter: '?status=new',
  },
  InternalTransfersCnt: {
    key: 'internal-transfer-waiting',
    color: '#F9B115',
    path: '/internal-transfer',
    filter: '?status=TRANSACTION_REQUEST_STATUS_WAITING_FOR_APPROVE',
  },
  VerificationCardCnt: {
    key: 'verification-card',
    color: '#F9B115',
    path: '/card-verifications',
  },
  GiftShopOrdersCnt: {
    key: 'gift-shop-orders',
    color: '#F9B115',
    path: '/gift-orders',
  },
  PaymentTrafficAlertCnt: {
    key: 'payment-traffic-warning',
    color: '#E55353',
    path: '/paymentTrafficMonitoring',
    filter: '?preset=last_30m&filterField_status=alert',
  },
  PaymentTrafficWarningCnt: {
    key: 'payment-traffic-alert',
    color: '#F9B115',
    path: '/paymentTrafficMonitoring',
    filter: '?preset=last_30m&filterField_status=warning',
  },
}

export const countersMounted = createEvent()

export const getCounters = fx<void, Counters>({
  method: 'GET',
  url: `/counters`,
})

export const $counters = restore<NotificationItem[]>(
  getCounters.doneData.map((a) => {
    const keys = Object.keys(a.body) as (keyof typeof mapCounterObject)[]

    const result: NotificationItem[] = keys.map((key) => {
      return {
        ...mapCounterObject[key],
        count: a.body[key],
      }
    })

    return result
  }),
  [],
)

sample({
  clock: countersMounted,
  fn: () => {
    return {
      url: `/counters`,
    }
  },
  target: getCounters,
})

export const setRequestSuccess = createEvent<boolean>()

export const setRequestError = createEvent<Error | null>()

export const $isRequestSuccess = restore(setRequestSuccess, false)

export const $requestError = restore(setRequestError, null)

export const notificationMounted = createEvent<{ api: NotificationInstance }>()

export const showNotification = createEvent<NotificationPayload>()

export const $notification = createStore<{
  api: NotificationInstance | null
}>({
  api: null,
}).on(notificationMounted, (_, payload) => payload)

export const showNotificationFx = createEffect(
  ({
    api,
    payload,
  }: {
    api: NotificationInstance
    payload: NotificationPayload
  }) => {
    api[payload.type]({
      message: payload?.message ?? '',
      description: payload?.description ?? '',
      placement: payload.placement ?? 'bottom',
      duration: payload.duration ?? 7,
    })
  },
)

sample({
  clock: showNotification,
  source: $notification,
  fn: ({ api }, payload) => ({
    api: api!,
    payload,
  }),
  target: showNotificationFx,
})
